import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { authAPI, casinoAPI } from '@/services/api';
import { APP_CONFIG } from '@/config/app';
import { STORAGE_KEYS } from '@/config/app';
import { t } from '@/i18n';

const TABLE_TYPES = new Set(['table', 'blackjack', 'roulette', 'baccarat', 'poker']);

const asNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const readStoredPlayer = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEYS.player) || 'null');
  } catch {
    return null;
  }
};

const mapGame = (game, index = 0) => ({
  id: String(game.id || game.code || ''),
  code: String(game.code || game.id || ''),
  gameid: String(game.gameid || game.code || game.id || ''),
  name: String(game.name || game.code || 'Unknown game'),
  provider: String(game.provider || game.providerName || game.providerCode || 'Unknown provider'),
  providerName: String(game.providerName || game.provider || ''),
  providerCode: String(game.providerCode || ''),
  providerId: String(game.providerId || ''),
  fun: Boolean(game.fun),
  type: String(game.type || 'slot'),
  featured: index < 12,
  created_at: game.created_at || null,
  thumbnail: String(game.thumbnail || APP_CONFIG.gamePlaceholderImage),
});

export const useGamesStore = defineStore('games', () => {
  const storedPlayer = readStoredPlayer();
  const providers = ref([]);
  const games = ref([]);
  const catalogLoaded = ref(false);
  const currentGame = ref(null);
  const gameSession = ref(null);
  const launcherUrl = ref('');
  const balance = ref(asNumber(storedPlayer?.balance, 0));
  const bonus = ref(asNumber(storedPlayer?.bonus, 0));
  const currency = ref(String(storedPlayer?.currency || 'ARS'));
  const balanceLoading = ref(false);
  const balanceLoaded = ref(Boolean(storedPlayer));
  const gameHistory = ref([]);
  const loading = ref(false);
  const error = ref(null);

  const slotGames = computed(() => games.value.filter((game) => game.type === 'slot'));
  const tableGames = computed(() => games.value.filter((game) => TABLE_TYPES.has(game.type)));
  const liveGames = computed(() => games.value.filter((game) => game.type.startsWith('live')));
  const featuredGames = computed(() => games.value.filter((game) => game.featured));

  const clearError = () => {
    error.value = null;
  };

  const fetchGames = async ({ operator = APP_CONFIG.operator } = {}) => {
    loading.value = true;
    clearError();

    try {
      const catalog = await casinoAPI.getCatalog({ operator });
      if (!catalog.success) {
        error.value = catalog.error.message;
        return { success: false, error: catalog.error.message };
      }

      providers.value = catalog.data.providers;
      games.value = catalog.data.games.map((game, index) => mapGame(game, index));

      if (catalog.data.providerErrors.length > 0) {
        console.warn('Provider game loading errors:', catalog.data.providerErrors);
      }

      return {
        success: true,
        data: {
          providers: providers.value,
          games: games.value,
          providerErrors: catalog.data.providerErrors,
        },
      };
    } catch (err) {
      error.value = err?.message || t('errors.games.load');
      return { success: false, error: error.value };
    } finally {
      loading.value = false;
      catalogLoaded.value = true;
    }
  };

  const fetchGameDetails = async (gameId) => {
    const normalizedId = String(gameId || '').trim();
    if (!normalizedId) {
      return { success: false, error: t('errors.games.invalidId') };
    }

    let game = games.value.find((item) => item.id === normalizedId || item.code === normalizedId);
    if (!game && games.value.length === 0) {
      const loaded = await fetchGames();
      if (!loaded.success) {
        return loaded;
      }
      game = games.value.find((item) => item.id === normalizedId || item.code === normalizedId);
    }

    if (!game) {
      return { success: false, error: t('errors.games.notFound') };
    }

    currentGame.value = game;
    return { success: true, data: game };
  };

  const fetchBalance = async ({ operator = APP_CONFIG.operator } = {}) => {
    clearError();
    balanceLoading.value = true;

    const response = await authAPI.balance({ operator });
    if (!response.success) {
      error.value = response.error.message;
      balanceLoading.value = false;
      return { success: false, error: response.error.message };
    }

    balance.value = asNumber(response.data.balance, 0);
    bonus.value = asNumber(response.data.bonus, 0);
    currency.value = String(response.data.currency || 'ARS');
    balanceLoaded.value = true;
    balanceLoading.value = false;

    return {
      success: true,
      data: {
        balance: balance.value,
        bonus: bonus.value,
        currency: currency.value,
      },
    };
  };

  const fetchGameHistory = async () => {
    gameHistory.value = [];
    return { success: true, data: [] };
  };

  const startGameSession = async (gameId) => {
    const gameResult = await fetchGameDetails(gameId);
    if (!gameResult.success) {
      return gameResult;
    }

    gameSession.value = {
      id: Date.now(),
      gameId: gameResult.data.id,
      startedAt: new Date().toISOString(),
    };

    return { success: true, data: gameSession.value };
  };

  const endGameSession = () => {
    gameSession.value = null;
    launcherUrl.value = '';
  };

  const loadLauncherUrl = async ({ gameid, mode, token, lang, operator = APP_CONFIG.operator }) => {
    clearError();
    const response = await casinoAPI.getLauncherUrl({ gameid, mode, token, lang, operator });
    if (!response.success) {
      error.value = response.error.message;
      return { success: false, error: response.error.message };
    }

    launcherUrl.value = response.data.url;
    return { success: true, data: response.data };
  };

  const clearCurrentGame = () => {
    currentGame.value = null;
    launcherUrl.value = '';
  };

  return {
    providers,
    games,
    catalogLoaded,
    currentGame,
    gameSession,
    launcherUrl,
    balance,
    bonus,
    currency,
    balanceLoading,
    balanceLoaded,
    gameHistory,
    loading,
    error,
    slotGames,
    tableGames,
    liveGames,
    featuredGames,
    fetchGames,
    fetchGameDetails,
    fetchBalance,
    fetchGameHistory,
    startGameSession,
    endGameSession,
    loadLauncherUrl,
    clearCurrentGame,
    clearError,
  };
});
